import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Globe,
  Package,
  Users,
  Clock,
  Shield,
  Lock,
  Heart,
  Zap,
  Headphones,
  ArrowRight,
} from 'lucide-react';
import heroBg from '../assets/auth/logistics-hero.png';
import '../styles/about-page.css';

const STAT_CARDS = [
  { Icon: Globe, value: '100+', label: 'Countries served' },
  { Icon: Package, value: '10K+', label: 'Shipments delivered' },
  { Icon: Users, value: '500+', label: 'Happy clients' },
  { Icon: Clock, value: '24/7', label: 'Customer support' },
];

const VALUES = [
  {
    Icon: Shield,
    title: 'Reliability',
    description: 'Consistent execution and clear communication on every shipment.',
  },
  {
    Icon: Lock,
    title: 'Security',
    description: 'Controlled handling, compliance-aware processes, and vetted partners.',
  },
  {
    Icon: Heart,
    title: 'Customer focus',
    description: 'Solutions shaped around your timelines, lanes, and cargo profile.',
  },
  {
    Icon: Zap,
    title: 'Efficiency',
    description: 'Streamlined booking, tracking, and coordination across air, sea, and land.',
  },
  {
    Icon: Globe,
    title: 'Global reach',
    description: 'Network coverage across major trade lanes with local expertise.',
  },
];

const WAREHOUSE_IMG = '/images/about/logistics-hub.jpg';

function AboutCtaMap() {
  return (
    <div className="about-cta__map" aria-hidden>
      <svg viewBox="0 0 220 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M20 75 Q55 40 95 55 T165 45 T205 70"
          stroke="rgba(125,211,252,0.55)"
          strokeWidth="1.5"
          strokeDasharray="4 4"
          fill="none"
        />
        <path
          d="M35 90 Q80 55 120 70 T190 60"
          stroke="rgba(96,165,250,0.45)"
          strokeWidth="1.2"
          strokeDasharray="3 5"
          fill="none"
        />
        <ellipse cx="110" cy="58" rx="88" ry="38" fill="rgba(56,189,248,0.12)" />
        <circle cx="58" cy="52" r="5" fill="#38bdf8" opacity="0.9" />
        <circle cx="118" cy="44" r="4" fill="#7dd3fc" opacity="0.95" />
        <circle cx="162" cy="62" r="4.5" fill="#38bdf8" opacity="0.85" />
        <circle cx="92" cy="72" r="3.5" fill="#bae6fd" opacity="0.9" />
        <path
          d="M40 88 L52 52 M112 42 L118 44 M158 66 L162 62"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth="0.8"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

const About = () => {
  return (
    <div className="about-page">
      <section className="about-hero" aria-labelledby="about-hero-title">
        <div className="about-hero__bg" style={{ backgroundImage: `url(${heroBg})` }} />
        <div className="about-hero__overlay" />
        <div className="about-hero__inner">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="about-hero__crumb">
              <Link to="/">Home</Link>
              <span aria-hidden> &gt; </span>
              About us
            </p>
            <h1 id="about-hero-title" className="about-hero__title">
              About Jet Shipping Cargo
            </h1>
            <p className="about-hero__lead">
              Delivering possibilities. Connecting the world. We are committed to providing reliable, secure and
              efficient cargo solutions across the globe.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="about-shell">
        <section className="about-who" aria-labelledby="about-who-title">
          <div>
            <span className="about-who__eyebrow">Who we are</span>
            <h2 id="about-who-title" className="about-who__title">
              Your trusted partner in global logistics
            </h2>
            <p className="about-who__text">
              Jet Shipping Cargo is a freight forwarding team based in Kuala Lumpur, Malaysia, focused on air cargo,
              sea cargo, FCL, and LCL—helping businesses move goods across Southeast Asia and worldwide. We combine
              regional access to ports and airports with a customer-first mindset: clear updates, competitive options,
              and careful handling from booking to delivery.
            </p>
            <Link to="/services" className="about-who__link">
              Learn more about our services
              <ArrowRight className="w-4 h-4" aria-hidden />
            </Link>
          </div>

          <div className="about-who__stats">
            {STAT_CARDS.map(({ Icon, value, label }, index) => (
              <motion.div
                key={label}
                className="about-stat-card"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index, duration: 0.35 }}
              >
                <Icon className="w-7 h-7" strokeWidth={1.85} aria-hidden />
                <strong>{value}</strong>
                <span>{label}</span>
              </motion.div>
            ))}
          </div>

          <div className="about-who__image">
            <img src={WAREHOUSE_IMG} alt="" loading="lazy" decoding="async" />
          </div>
        </section>

        <section className="about-values" aria-labelledby="about-values-title">
          <header className="about-values__head">
            <span className="about-values__eyebrow">Our values</span>
            <h2 id="about-values-title" className="about-values__title">
              <span>Why choose Jet Shipping Cargo?</span>
            </h2>
          </header>
          <div className="about-values__grid">
            {VALUES.map(({ Icon, title, description }, index) => (
              <motion.div
                key={title}
                className="about-value"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * index, duration: 0.35 }}
              >
                <div className="about-value__icon">
                  <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
              </motion.div>
            ))}
          </div>
        </section>
      </div>

      <section className="about-cta" aria-labelledby="about-cta-title">
        <div className="about-cta__icon-wrap">
          <Headphones className="w-6 h-6" strokeWidth={1.75} aria-hidden />
        </div>
        <div className="about-cta__body">
          <h2 id="about-cta-title">Let&apos;s move your cargo, anywhere in the world</h2>
          <p>
            Seamless, affordable cargo solutions—from first mile to final delivery—with a team that stays close to
            your shipment.
          </p>
          <Link to="/contact?intent=quote" className="about-cta__btn">
            Get a quick quote
            <ArrowRight className="w-4 h-4" aria-hidden />
          </Link>
        </div>
        <AboutCtaMap />
      </section>
    </div>
  );
};

export default About;
